// src/Resume_Submission/utils/booleanSearchParser.js

/**
 * Parses a query string into a postfix token array using the Shunting-Yard algorithm.
 * Supports AND, OR, NOT, grouping with parentheses (), exact phrases in quotes,
 * and inserts implicit AND operators between adjacent values.
 *
 * @param {string} query The search query string
 * @returns {Array<Object>} Postfix token queue
 */
export const parseBooleanQuery = (query) => {
  if (!query || !query.trim()) return [];

  // Tokenize: Match double quoted phrases, parentheses, operators, and alphanumeric/symbol words
  const regex = /("[^"\\]*(?:\\.[^"\\]*)*"|\(|\)|AND|OR|NOT|[\w+-]+)/gi;
  const tokens = [];
  let match;
  while ((match = regex.exec(query)) !== null) {
    let token = match[0];
    if (token.startsWith('"') && token.endsWith('"')) {
      tokens.push({ type: 'PHRASE', value: token.slice(1, -1).toLowerCase().trim() });
    } else if (token === '(') {
      tokens.push({ type: 'LPAREN' });
    } else if (token === ')') {
      tokens.push({ type: 'RPAREN' });
    } else {
      const upperToken = token.toUpperCase();
      if (upperToken === 'AND') {
        tokens.push({ type: 'AND' });
      } else if (upperToken === 'OR') {
        tokens.push({ type: 'OR' });
      } else if (upperToken === 'NOT') {
        tokens.push({ type: 'NOT' });
      } else {
        tokens.push({ type: 'WORD', value: token.toLowerCase() });
      }
    }
  }

  // Insert Implicit AND between value-like tokens and start-like tokens
  const tokensWithAnd = [];
  const isValueLike = (t) => t && (t.type === 'WORD' || t.type === 'PHRASE' || t.type === 'RPAREN');
  const isStartLike = (t) => t && (t.type === 'WORD' || t.type === 'PHRASE' || t.type === 'LPAREN' || t.type === 'NOT');

  for (let i = 0; i < tokens.length; i++) {
    if (i > 0 && isValueLike(tokensWithAnd[tokensWithAnd.length - 1]) && isStartLike(tokens[i])) {
      tokensWithAnd.push({ type: 'AND' });
    }
    tokensWithAnd.push(tokens[i]);
  }

  // Shunting-yard Infix-to-Postfix conversion
  const outputQueue = [];
  const operatorStack = [];
  const precedence = {
    'NOT': 3,
    'AND': 2,
    'OR': 1
  };

  for (const token of tokensWithAnd) {
    if (token.type === 'WORD' || token.type === 'PHRASE') {
      outputQueue.push(token);
    } else if (token.type === 'NOT' || token.type === 'AND' || token.type === 'OR') {
      while (operatorStack.length > 0) {
        const topType = operatorStack[operatorStack.length - 1].type;
        if (topType === 'LPAREN') break;
        if (precedence[topType] >= precedence[token.type]) {
          outputQueue.push(operatorStack.pop());
        } else {
          break;
        }
      }
      operatorStack.push(token);
    } else if (token.type === 'LPAREN') {
      operatorStack.push(token);
    } else if (token.type === 'RPAREN') {
      let foundLparen = false;
      while (operatorStack.length > 0) {
        if (operatorStack[operatorStack.length - 1].type === 'LPAREN') {
          operatorStack.pop();
          foundLparen = true;
          break;
        } else {
          outputQueue.push(operatorStack.pop());
        }
      }
    }
  }

  while (operatorStack.length > 0) {
    const top = operatorStack.pop();
    if (top.type !== 'LPAREN') {
      outputQueue.push(top);
    }
  }

  return outputQueue;
};

/**
 * Evaluates candidate records against the parsed postfix AST search queue.
 *
 * @param {Object} candidate The candidate object
 * @param {Array<Object>} postfixQueue Postfix query token array
 * @param {Function} checkMatchFn A callback matching a single term against a candidate record
 * @returns {boolean} Whether the candidate matches the boolean expression
 */
export const matchCandidateWithBooleanQuery = (candidate, postfixQueue, checkMatchFn) => {
  if (!postfixQueue || postfixQueue.length === 0) return true;
  const stack = [];

  for (const token of postfixQueue) {
    if (token.type === 'WORD' || token.type === 'PHRASE') {
      stack.push(checkMatchFn(candidate, token.value));
    } else if (token.type === 'NOT') {
      const val = stack.length > 0 ? stack.pop() === true : false;
      stack.push(!val);
    } else if (token.type === 'AND') {
      const b = stack.length > 0 ? stack.pop() === true : false;
      const a = stack.length > 0 ? stack.pop() === true : false;
      stack.push(a && b);
    } else if (token.type === 'OR') {
      const b = stack.length > 0 ? stack.pop() === true : false;
      const a = stack.length > 0 ? stack.pop() === true : false;
      stack.push(a || b);
    }
  }

  return stack.length > 0 ? stack[0] === true : true;
};
